import { addDocument, getDocumentDetails, deleteDocumentFile, getDataSet, fetchDocSettingsAPI, fetchAssistantParametersAPI } from '../apis';

/**
 * Default document settings (fallback)
 */
const DEFAULT_DOC_SETTINGS = {
  chunkSize: 150,
  version: 1,
  chunk: 700,
  embeddingDeployment: "smart-agent-embedding-ada",
  numList: 1,
  chunkOverlap: 320,
  embeddingModel: "text-embedding-ada-002",
  mapSummaryTemplate: "Provide a structured summary of the following content. \n Use the information to create a clear, concise, and brief informative summary that covers all key points, themes, and statistics. \n Do not exceed 3-4 sentences for the entire summary. generate as quickly as possible. \n {content} \n \n Summary:",
  imageSummarizationPrompt: "Act as an image analysis expert. Your task is to assess the content of an image in detail and provide a structured, high-quality summary based on its textual and visual elements.\n\n1. For Text-Heavy Images:\n   - Extract all visible text exactly as it appears without altering spelling.\n   - Analyze structured data, such as tables or lists, interpreting rows and columns with all relevant numbers and their units.\n   - Present a detailed yet concise interpretation of the content, omitting introductory phrases. Ensure that the text flows naturally without phrases like \"Here is the extracted text.\"\n\n2. For Visually Oriented Images:\n   - Summarize high-level information about the visual content, identifying themes, icons, and significant details.\n   - Include numerical data (like percentages, units, measurements) if it's essential for understanding.\n   - Deliver a concise, meaningful summary of key visual elements without going into excessive detail.\n\nGuidelines:\n- Avoid introductory phrases or redundant information.\n- Maintain a clean structure for readability, ensuring that each section is meaningful and relevant.",
  reduceSummaryTemplate: "Below is a collection of summaries based on different sections of a document.\n Use these to produce a single, cohesive summary that thoroughly covers all significant themes, key points, and notable statistics.\n Structure the summary for maximum clarity and flow, using organized sections, bullet points, or paragraphs for readability. \n - Maintain a concise, high-quality flow of information. \n - Limit the final summary to a maximum of 500 words. \n - Ensure the final output is comprehensive,separated paragraphs,well-structured and visually engaging with markdown format. \n\n {doc_summaries} \n\n Final Summary:",
  trainingMethod: "Multi-modal",
  trainingModel: "gpt-4o-mini",
  serviceAgentEnabled: false,
  docChunkEnabled: 0,
  docAsChunkEnabled: false,
  pageAsChunkEnabled: true,
  csvColumns: ""
};

/**
 * Default assistant settings (fallback)
 */
const DEFAULT_ASSISTANT_SETTINGS = {
  chunkingStrategy: "auto",
  chunkSize: 500,
  chunkOverlap: 320
};

/**
 * Finds and returns the reference dataset
 * @returns {Promise<Object|null>} Reference dataset or null if not found
 */
const findReferenceDataset = async () => {
  try {
    const response = await getDataSet();
    if (response?.data) {
      return response.data.find(dataset => dataset.isReferenceDataset === true);
    }
    return null;
  } catch (error) {
    console.error('Error finding reference dataset for document upload:', error);
    return null;
  }
};

// Simple approach - no caching, just call APIs when needed

/**
 * Gets document parameters from reference dataset
 * @param {string} quickChatType - Type of QuickChat: 'quickChat' or 'assistantQuickChat'
 * @returns {Promise<Object>} Document parameters object
 */
const getReferenceDocumentParameters = async (quickChatType) => {
  try {
    console.log(`Fetching parameters for ${quickChatType} document upload`);

    const referenceDataset = await findReferenceDataset();
    
    if (!referenceDataset) {
      console.log('No reference dataset found for document upload, using defaults');
      return quickChatType === 'assistantQuickChat' 
        ? DEFAULT_ASSISTANT_SETTINGS 
        : DEFAULT_DOC_SETTINGS;
    }

    console.log('Found reference dataset for document upload:', referenceDataset.datasetName);
    
    if (quickChatType === 'assistantQuickChat') {
      // For Assistant QuickChat, get assistant parameters
      try {
        const assistantResponse = await fetchAssistantParametersAPI(referenceDataset.id);
        console.log('Raw API response for assistant settings:', assistantResponse);
        console.log('Response structure - data property:', assistantResponse?.data);
        console.log('Response structure - status:', assistantResponse?.status);
        console.log('Response structure - all keys:', Object.keys(assistantResponse || {}));
        
        let assistantParams = assistantResponse?.data || assistantResponse; // Try data property first, fallback to response
        console.log('Assistant parameters from reference dataset:', assistantParams);
        
        // Check if the response is an array and extract the first element
        if (Array.isArray(assistantParams) && assistantParams.length > 0) {
          console.log('Assistant API response is an array, extracting first element');
          assistantParams = assistantParams[0];
          console.log('Extracted assistant parameters object:', assistantParams);
        }
        
        if (!assistantParams || Object.keys(assistantParams).length === 0) {
          console.log('⚠️ No assistant parameters found in API response, using defaults');
          return DEFAULT_ASSISTANT_SETTINGS;
        }
        
        console.log('✅ Using reference dataset assistant parameters for document upload');
        
        // Debug each parameter mapping
        console.log('Assistant parameter mapping debug:');
        console.log('- chunkingStrategy:', assistantParams.chunkingStrategy, '|| default:', DEFAULT_ASSISTANT_SETTINGS.chunkingStrategy);
        console.log('- chunk (for chunkSize):', assistantParams.chunk, '|| default:', DEFAULT_ASSISTANT_SETTINGS.chunkSize);
        console.log('- chunkOverlap:', assistantParams.chunkOverlap, '|| default:', DEFAULT_ASSISTANT_SETTINGS.chunkOverlap);
        
        const finalAssistantParams = {
          chunkingStrategy: assistantParams.chunkingStrategy || DEFAULT_ASSISTANT_SETTINGS.chunkingStrategy,
          chunkSize: assistantParams.chunk || DEFAULT_ASSISTANT_SETTINGS.chunkSize,
          chunkOverlap: assistantParams.chunkOverlap || DEFAULT_ASSISTANT_SETTINGS.chunkOverlap
        };
        
        console.log('Final constructed assistant parameters object:', finalAssistantParams);
        return finalAssistantParams;
      } catch (error) {
        console.log('⚠️ Failed to fetch assistant parameters from reference dataset, using defaults');
        return DEFAULT_ASSISTANT_SETTINGS;
      }
    } else {
      // For regular QuickChat, get document settings
      try {
        const docResponse = await fetchDocSettingsAPI(referenceDataset.id);
        console.log('Raw API response for document settings:', docResponse);
        console.log('Response structure - data property:', docResponse?.data);
        console.log('Response structure - status:', docResponse?.status);
        console.log('Response structure - all keys:', Object.keys(docResponse || {}));
        
        let docParams = docResponse?.data || docResponse; // Try data property first, fallback to response
        console.log('Document parameters from reference dataset:', docParams);
        
        // Check if the response is an array and extract the first element
        if (Array.isArray(docParams) && docParams.length > 0) {
          console.log('API response is an array, extracting first element');
          docParams = docParams[0];
          console.log('Extracted document parameters object:', docParams);
        }
        
        if (!docParams || Object.keys(docParams).length === 0) {
          console.log('⚠️ No document parameters found in API response, using defaults');
          return DEFAULT_DOC_SETTINGS;
        }
        
        console.log('✅ Using reference dataset document parameters for document upload');
        
        // Debug each parameter mapping
        console.log('Parameter mapping debug:');
        console.log('- chunkSize:', docParams.chunkSize, '|| default:', DEFAULT_DOC_SETTINGS.chunkSize);
        console.log('- chunk:', docParams.chunk, '|| default:', DEFAULT_DOC_SETTINGS.chunk);
        console.log('- chunkOverlap:', docParams.chunkOverlap, '|| default:', DEFAULT_DOC_SETTINGS.chunkOverlap);
        console.log('- embeddingDeployment:', docParams.embeddingDeployment, '|| default:', DEFAULT_DOC_SETTINGS.embeddingDeployment);
        console.log('- embeddingModel:', docParams.embeddingModel, '|| default:', DEFAULT_DOC_SETTINGS.embeddingModel);
        
        const finalParams = {
          ...DEFAULT_DOC_SETTINGS,
          chunkSize: docParams.chunkSize || DEFAULT_DOC_SETTINGS.chunkSize,
          chunk: docParams.chunk || DEFAULT_DOC_SETTINGS.chunk,
          chunkOverlap: docParams.chunkOverlap || DEFAULT_DOC_SETTINGS.chunkOverlap,
          embeddingDeployment: docParams.embeddingDeployment || DEFAULT_DOC_SETTINGS.embeddingDeployment,
          embeddingModel: docParams.embeddingModel || DEFAULT_DOC_SETTINGS.embeddingModel,
          mapSummaryTemplate: docParams.mapSummaryTemplate || DEFAULT_DOC_SETTINGS.mapSummaryTemplate,
          imageSummarizationPrompt: docParams.imageSummarizationPrompt || DEFAULT_DOC_SETTINGS.imageSummarizationPrompt,
          reduceSummaryTemplate: docParams.reduceSummaryTemplate || DEFAULT_DOC_SETTINGS.reduceSummaryTemplate,
          trainingMethod: docParams.trainingMethod || DEFAULT_DOC_SETTINGS.trainingMethod,
          trainingModel: docParams.trainingModel || DEFAULT_DOC_SETTINGS.trainingModel,
          serviceAgentEnabled: docParams.serviceAgentEnabled !== undefined ? docParams.serviceAgentEnabled : DEFAULT_DOC_SETTINGS.serviceAgentEnabled,
          docChunkEnabled: docParams.docChunkEnabled !== undefined ? docParams.docChunkEnabled : DEFAULT_DOC_SETTINGS.docChunkEnabled,
          docAsChunkEnabled: docParams.docAsChunkEnabled !== undefined ? docParams.docAsChunkEnabled : DEFAULT_DOC_SETTINGS.docAsChunkEnabled,
          pageAsChunkEnabled: docParams.pageAsChunkEnabled !== undefined ? docParams.pageAsChunkEnabled : DEFAULT_DOC_SETTINGS.pageAsChunkEnabled,
          csvColumns: docParams.csvColumns || DEFAULT_DOC_SETTINGS.csvColumns
        };
        
        console.log('Final constructed parameters object:', finalParams);
        return finalParams;
      } catch (error) {
        console.log('⚠️ Failed to fetch document parameters from reference dataset, using defaults');
        return DEFAULT_DOC_SETTINGS;
      }
    }
  } catch (error) {
    console.error('Error getting reference document parameters:', error);
    return quickChatType === 'assistantQuickChat' 
      ? DEFAULT_ASSISTANT_SETTINGS 
      : DEFAULT_DOC_SETTINGS;
  }
};

/**
 * Check if a file name already exists in the dataset
 * @param {string} fileName - The file name to check
 * @param {string} datasetId - The dataset ID
 * @returns {Promise<boolean>} True if file exists, false otherwise
 */
export const checkFileNameExists = async (fileName, datasetId) => {
  try {
    const documents = await checkDocumentTrainingStatus(datasetId);
    return documents.some(doc => doc.name === fileName);
  } catch (error) {
    console.error('Error checking file name existence:', error);
    // Return false to allow upload if check fails (graceful degradation)
    return false;
  }
};

/**
 * Upload a file to a dataset for Quick Chat
 * @param {File} file - The file to upload
 * @param {string} datasetId - The dataset ID
 * @param {function} onProgress - Progress callback function
 * @param {boolean} skipDuplicateCheck - Skip duplicate check (for retry scenarios)
 * @param {string} quickChatType - Type of quick chat: 'quickChat' or 'assistantQuickChat'
 * @returns {Object} Uploaded file information
 */
export const uploadFileToDataset = async (file, datasetId, onProgress, skipDuplicateCheck = false, quickChatType = 'quickChat') => {
  // Check for duplicate file name before upload
  if (!skipDuplicateCheck) {
    const fileExists = await checkFileNameExists(file.name, datasetId);
    if (fileExists) {
      throw new Error(`A file with the name "${file.name}" already exists in this dataset. Please rename the file or remove the existing one first.`);
    }
  }

  const formData = new FormData();
  formData.append('files', file);
  formData.append('documentType', 'PDF');
  
  // Add file options for quick chat uploads
  const options = {
    [file.name]: {
      isDomainDoc: false,
      isDataDoc: true, // Default to content files for quick chat
    }
  };
  formData.append('options', JSON.stringify(options));

  // Get parameters from reference dataset
  const referenceParams = await getReferenceDocumentParameters(quickChatType);
  console.log('=== DOCUMENT UPLOAD PARAMETERS DEBUG ===');
  console.log('QuickChat Type:', quickChatType);
  console.log('Reference Parameters Retrieved:', referenceParams);
  console.log('Is using defaults?', referenceParams === DEFAULT_DOC_SETTINGS || referenceParams === DEFAULT_ASSISTANT_SETTINGS);
  console.log('==========================================');
  // Use reference dataset parameters based on quick chat type
  if (quickChatType === 'assistantQuickChat') {
    // Assistant Quick Chat - use assistant parameters from reference dataset
    const assistantSettings = {
      chunkingStrategy: referenceParams.chunkingStrategy,
      chunkSize: referenceParams.chunkSize,
      chunkOverlap: referenceParams.chunkOverlap
    };
    formData.append('assistantSettings', JSON.stringify(assistantSettings));
    console.log('Using assistant parameters from reference dataset for document upload:', assistantSettings);
  } else {
    // Regular Quick Chat - use document parameters from reference dataset
    const docSettings = {
      createdAt: new Date().toISOString(),
      createdBy: "c3c6a959-9613-442a-96f2-8ef6a7f64a80", // This should be dynamic based on current user
      chunkSize: referenceParams.chunkSize,
      version: referenceParams.version,
      chunk: referenceParams.chunk,
      embeddingDeployment: referenceParams.embeddingDeployment,
      numList: referenceParams.numList,
      chunkOverlap: referenceParams.chunkOverlap,
      embeddingModel: referenceParams.embeddingModel,
      mapSummaryTemplate: referenceParams.mapSummaryTemplate,
      imageSummarizationPrompt: referenceParams.imageSummarizationPrompt,
      reduceSummaryTemplate: referenceParams.reduceSummaryTemplate,
      trainingMethod: referenceParams.trainingMethod,
      trainingModel: referenceParams.trainingModel,
      serviceAgentEnabled: referenceParams.serviceAgentEnabled,
      docChunkEnabled: referenceParams.docChunkEnabled,
      docAsChunkEnabled: referenceParams.docAsChunkEnabled,
      pageAsChunkEnabled: referenceParams.pageAsChunkEnabled,
      csvColumns: referenceParams.csvColumns,
      datasetId: datasetId
    };
    formData.append('docSettings', JSON.stringify(docSettings));
    console.log('Using document parameters from reference dataset for document upload:', docSettings);
  }

  try {
    const response = await addDocument(formData, datasetId);

    const documentId = response.data.id || generateFileId(file);
    
    // Store file size in localStorage for later retrieval
    storeFileSize(file.name, datasetId, file.size);
    
    return {
      id: documentId,
      _id: documentId, // Store document ID for deletion operations
      name: file.name,
      size: file.size,
      status: 'queued',
      uploadedAt: new Date().toISOString(),
      error: null,
      progress: 0
    };
  } catch (error) {
    throw new Error(`Failed to upload file: ${error.message}`);
  }
};

/**
 * Store file size in localStorage for later retrieval
 * @param {string} documentName - The document name
 * @param {string} datasetId - The dataset ID  
 * @param {number} sizeInBytes - File size in bytes
 */
const storeFileSize = (documentName, datasetId, sizeInBytes) => {
  try {
    const key = `fileSize_${datasetId}_${documentName}`;
    localStorage.setItem(key, sizeInBytes.toString());
  } catch (error) {
    console.warn('Failed to store file size in localStorage:', error);
  }
};

/**
 * Retrieve file size from localStorage
 * @param {string} documentName - The document name
 * @param {string} datasetId - The dataset ID
 * @returns {number} File size in bytes, or 0 if not found
 */
const getStoredFileSize = (documentName, datasetId) => {
  try {
    const key = `fileSize_${datasetId}_${documentName}`;
    const stored = localStorage.getItem(key);
    return stored ? parseInt(stored, 10) : 0;
  } catch (error) {
    console.warn('Failed to retrieve file size from localStorage:', error);
    return 0;
  }
};

/**
 * Check the training status of all documents in a dataset
 * @param {string} datasetId - The dataset ID
 * @returns {Array} Array of document status objects
 */
export const checkDocumentTrainingStatus = async (datasetId) => {
  try {
    const response = await getDocumentDetails(datasetId);
    
    if (response?.data) {
      // Map the document data to include training status
      return response.data.map(doc => {
        // Try to get file size from localStorage first, then fall back to backend data
        const storedSizeBytes = getStoredFileSize(doc.documentName, datasetId);
        const backendSizeBytes = doc.fileSizeMB ? doc.fileSizeMB * 1024 * 1024 : 0;
        const finalSizeBytes = storedSizeBytes || backendSizeBytes;
        
        // Debug log to verify file sizes are working
        if (finalSizeBytes > 0) {
          console.log(`File size found for ${doc.documentName}: ${finalSizeBytes} bytes (${(finalSizeBytes / 1024 / 1024).toFixed(2)} MB)`);
        }
        
        return {
          id: doc._id || doc.id,
          name: doc.documentName,
          trainingStatus: doc.trainingStatus || 'Queued', // Queued, Training, Trained, Failed
          status: mapTrainingStatusToDisplayStatus(doc.trainingStatus),
          progress: getProgressFromTrainingStatus(doc.trainingStatus),
          error: doc.trainingStatus === 'Failed' ? 'Training failed' : null,
          documentType: doc.documentType,
          createdAt: doc.createdAt,
          updatedAt: doc.updatedAt,
          fileSizeMB: doc.fileSizeMB || 0, // Add file size from backend response  
          fileSizeBytes: finalSizeBytes, // Use stored size if available, otherwise backend size
          size: finalSizeBytes // Add size field for compatibility
        };
      });
    }
    
    return [];
  } catch (error) {
    console.error('Failed to check document training status:', error);
    return [];
  }
};

/**
 * Map training status to display status
 * @param {string} trainingStatus - The training status from API
 * @returns {string} Display status
 */
const mapTrainingStatusToDisplayStatus = (trainingStatus) => {
  switch (trainingStatus) {
    case 'Queued':
      return 'queued';
    case 'Training':
      return 'processing';
    case 'Trained':
      return 'completed';
    case 'Failed':
      return 'failed';
    case 'Deleting':
      return 'deleting';
    case 'Deleted':
      return 'deleted';
    default:
      return 'queued';
  }
};

/**
 * Get progress percentage from training status
 * @param {string} trainingStatus - The training status from API
 * @returns {number} Progress percentage
 */
const getProgressFromTrainingStatus = (trainingStatus) => {
  switch (trainingStatus) {
    case 'Queued':
      return 0;
    case 'Training':
      return 50;
    case 'Trained':
      return 100;
    case 'Failed':
      return 0;
    case 'Deleting':
      return 75; // Show some progress for deletion
    case 'Deleted':
      return 100; // Show complete for deleted files
    default:
      return 0;
  }
};

/**
 * Generate a unique file ID
 * @param {File} file - The file object
 * @returns {string} Generated file ID
 */
const generateFileId = (file) => {
  return `${file.name}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

/**
 * Validate file before upload
 * @param {File} file - The file to validate
 * @returns {Object} Validation result
 */
export const validateFile = (file) => {
  const errors = [];

  // Size validation (10MB limit)
  const maxSize = 10 * 1024 * 1024;
  if (file.size > maxSize) {
    errors.push('File size must be less than 10MB');
  }

  // Type validation - Only allow PDF and MD files
  const allowedTypes = [
    'text/markdown',
    'text/x-markdown',
    'application/pdf'
  ];

  // Allow .md files by extension if type is not recognized
  const isMarkdownByExtension = file.name && file.name.toLowerCase().endsWith('.md');
  const isPdfByExtension = file.name && file.name.toLowerCase().endsWith('.pdf');

  if (!allowedTypes.includes(file.type) && 
      !(isMarkdownByExtension && (!file.type || file.type === 'application/octet-stream' || file.type === '')) &&
      !(isPdfByExtension && (!file.type || file.type === 'application/octet-stream' || file.type === ''))) {
    errors.push('Unsupported file type. Please upload only PDF or Markdown (.md) files.');
  }

  // Name validation
  if (file.name.length > 255) {
    errors.push('File name must be less than 255 characters');
  }

  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * Delete a document from the dataset
 * @param {string} documentId - The document ID to delete
 * @param {string} datasetId - The dataset ID
 * @returns {Object} Delete response
 */
export const deleteDocumentFromDataset = async (documentId, datasetId) => {
  try {
    const response = await deleteDocumentFile({ 
      id: documentId, 
      datasetId: datasetId 
    });
    
    console.log('DELETE API raw response:', response);
    console.log('DELETE API response.data:', response?.data);
    console.log('DELETE API response.status:', response?.status);
    
    // Check for successful response - prioritize HTTP status code (200-299 range)
    if (response && response.status >= 200 && response.status < 300) {
      console.log('Delete API succeeded with status:', response.status);
      return { success: true, documentId, response };
    } else {
      console.warn('Delete API returned non-success status:', response?.status);
      console.warn('Delete API response:', response);
      throw new Error(`Delete operation failed - HTTP status: ${response?.status}`);
    }
  } catch (error) {
    console.error('Failed to delete document:', error);
    throw new Error(`Failed to delete document: ${error.message}`);
  }
};

/**
 * Poll for document deletion completion
 * @param {string} documentId - The document ID to check for deletion
 * @param {string} datasetId - The dataset ID
 * @param {function} onProgress - Progress callback function
 * @returns {Promise} Resolves when document is deleted
 */
export const pollDocumentDeletion = async (documentId, datasetId, onProgress) => {
  const maxAttempts = 30; // Maximum attempts (30 * 2 seconds = 1 minute)
  let attempts = 0;
  
  return new Promise((resolve, reject) => {
    const poll = async () => {
      try {
        attempts++;
        const documents = await checkDocumentTrainingStatus(datasetId);
        
        // Check if document still exists in the list
        const documentExists = documents.some(doc => doc.id === documentId);
        
        if (!documentExists) {
          // Document successfully deleted
          if (onProgress) {
            onProgress('completed');
          }
          resolve(true);
          return;
        }
        
        // Check if document status is "Deleting"
        const deletingDoc = documents.find(doc => doc.id === documentId);
        if (deletingDoc && deletingDoc.trainingStatus === 'Deleting') {
          if (onProgress) {
            onProgress('deleting');
          }
        }
        
        // Continue polling if document still exists and we haven't exceeded max attempts
        if (attempts < maxAttempts) {
          setTimeout(poll, 2000); // Poll every 2 seconds
        } else {
          // Timeout reached
          reject(new Error('Document deletion timeout - please refresh to check status'));
        }
        
      } catch (error) {
        console.error('Error polling document deletion:', error);
        reject(error);
      }
    };
    
    // Start polling
    poll();
  });
};

/**
 * Format file size for display
 * @param {number} bytes - Size in bytes
 * @returns {string} Formatted size string
 */
export const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

/**
 * Get file size for a document (from localStorage or fallback)
 * @param {string} documentName - The document name
 * @param {string} datasetId - The dataset ID
 * @param {number} fallbackSizeMB - Fallback size in MB from backend
 * @returns {number} File size in bytes
 */
export const getDocumentFileSize = (documentName, datasetId, fallbackSizeMB = 0) => {
  const storedSizeBytes = getStoredFileSize(documentName, datasetId);
  const fallbackSizeBytes = fallbackSizeMB * 1024 * 1024;
  return storedSizeBytes || fallbackSizeBytes;
};

/**
 * Document Training Status Polling Service
 * Polls at 5-second intervals until all documents are Trained or Failed
 */
export class DocumentStatusPoller {
  constructor(datasetId, onStatusUpdate, pollingInterval = 5000) {
    this.datasetId = datasetId;
    this.onStatusUpdate = onStatusUpdate;
    this.pollingInterval = pollingInterval;
    this.intervalId = null;
    this.isPolling = false;
  }

  startPolling() {
    if (this.isPolling) {
      return; // Already polling
    }

    this.isPolling = true;
    console.log(`Starting document training status polling for dataset: ${this.datasetId}`);

    this.intervalId = setInterval(async () => {
      try {
        const documents = await checkDocumentTrainingStatus(this.datasetId);
        
        if (documents.length > 0) {
          // Update status for all documents
          this.onStatusUpdate(documents);

          // Check if all documents are in final state (Trained or Failed)
          const allCompleted = documents.every(doc => 
            doc.trainingStatus === 'Trained' || doc.trainingStatus === 'Failed'
          );

          if (allCompleted) {
            console.log('All documents have completed training, stopping polling');
            this.stopPolling();
          }
        } else {
          // No documents found, stop polling
          this.stopPolling();
        }
      } catch (error) {
        console.error(`Status polling failed for dataset ${this.datasetId}:`, error);
      }
    }, this.pollingInterval);
  }

  stopPolling() {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
      this.isPolling = false;
      console.log(`Stopped document training status polling for dataset: ${this.datasetId}`);
    }
  }

  // Check if there are any documents still being processed
  static hasDocumentsInProgress(documents) {
    return documents.some(doc => 
      doc.trainingStatus === 'Queued' || doc.trainingStatus === 'Training' || doc.trainingStatus === 'Deleting'
    );
  }

  // Get training progress status for all documents
  static getTrainingProgress(documents) {
    if (!documents || documents.length === 0) {
      return { hasProgress: false, totalProgress: 0 };
    }

    const inProgress = documents.filter(doc => 
      doc.trainingStatus === 'Queued' || doc.trainingStatus === 'Training' || doc.trainingStatus === 'Deleting'
    );
    
    return {
      hasProgress: inProgress.length > 0,
      totalProgress: inProgress.length,
      completedCount: documents.filter(doc => doc.trainingStatus === 'Trained').length,
      failedCount: documents.filter(doc => doc.trainingStatus === 'Failed').length
    };
  }
}